/**
 * Moving the user to the first thing that needs fixing after a blocked
 * Continue / Submit.
 *
 * This lived inline in every screen that had a gate, which meant six copies of
 * the same twenty lines and no way to fix the rule once. They had already
 * drifted: some moved keyboard focus and some didn't, and section-level
 * blockers were centred — which pushes the banner at the TOP of a tall section
 * off-screen, the exact failure the rule exists to prevent.
 *
 * Screens now describe *what blocks them, in priority order* and this owns the
 * how. Room for the sticky page chrome comes from a global `scroll-margin-top`
 * in the app's CSS, so there is no class or ref convention to remember and an
 * ordinary in-page anchor link clears the header too.
 */

/** Placeholder for "whichever field is currently marked invalid". */
export const FIRST_INVALID_FIELD = Symbol('first-invalid-field')

export type ErrorTarget = HTMLElement | null | undefined | typeof FIRST_INVALID_FIELD

/**
 * Scroll to the first resolvable target, in the order given.
 *
 * Pass `null` for a blocker that isn't active this time, so the priority order
 * reads the same as the screen's own logic:
 *
 *   scrollToFirstError(
 *     regionSumOk ? null : regionSectionRef.current,  // banner outranks the field
 *     FIRST_INVALID_FIELD,
 *   )
 *
 * An element is aligned to its TOP; the invalid field is centred and given
 * focus, because scrolling alone tells a keyboard or screen-reader user
 * nothing. Waits a frame first so the error DOM has rendered.
 */
export function scrollToFirstError(...targets: ErrorTarget[]): void {
  scrollToFirstErrorWithin(null, ...targets)
}

/**
 * As `scrollToFirstError`, but the invalid-field lookup is confined to `scope`.
 * Use it where the form is embedded in a wider page — a site header or a second
 * form elsewhere would otherwise be able to win the "first invalid field" race.
 * `null` scope searches the whole document.
 */
export function scrollToFirstErrorWithin(
  scope: HTMLElement | null,
  ...targets: ErrorTarget[]
): void {
  window.setTimeout(() => {
    for (const target of targets) {
      if (target === FIRST_INVALID_FIELD) {
        const root: ParentNode = scope ?? document
        const field = root.querySelector<HTMLElement>('[aria-invalid="true"]')
        if (!field) continue
        field.scrollIntoView({ behavior: 'smooth', block: 'center' })
        // preventScroll so focus doesn't fight the smooth scroll above.
        field.focus({ preventScroll: true })
        return
      }
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' })
        return
      }
    }
  }, 50)
}
